import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Segment } from 'tea-component';
import { getLocale, useLocation } from 'umi';
import SvgIcon from '@home/components/SvgIcon';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { ISegmentCo, ISegmnetOption } from './type';
import CommonBubble from '@home/components/CommonBubble';
import './SegmentCo.scss';

const EMPTY_OPTIONS: ISegmnetOption[] = [];

const SegmentCo = (props: ISegmentCo) => {
  const isEN = getLocale() === 'en-US';
  const { config, data, changeData, children, className, style, childrenRenderMode, childrenMatchField } = props;
  const {
    options: configOptions = EMPTY_OPTIONS,
    dataKey = '',
    showOnTimeType,
    timeTypeOptionsMap,
    defaultValueMap,
  } = config;

  const { search } = useLocation();
  const contextObj = useContext(FilterContext);
  const { filterVal = {} } = contextObj || {};
  const timeType = (filterVal?.timeType || 'Day')?.toLowerCase();

  const currentOptions = useMemo(() => {
    if (timeTypeOptionsMap?.[timeType]) {
      return timeTypeOptionsMap[timeType];
    }
    return configOptions;
  }, [timeTypeOptionsMap, timeType, configOptions]);

  const getDefaultValueByTimeType = () => {
    // 优先级 1: URL 的 defaultTab 参数（跳转继承）
    const urlDefaultTab = new URLSearchParams(search).get('defaultTab');
    if (urlDefaultTab && currentOptions?.some(opt => opt.value === urlDefaultTab)) {
      return urlDefaultTab;
    }
    // 优先级 2: data 中已存的值
    const savedVal = dataKey && data?.[dataKey];
    if (savedVal && currentOptions?.some(opt => opt.value === savedVal)) {
      return savedVal;
    }
    // 优先级 3: timeType 对应的默认值
    const configDefault = defaultValueMap?.[timeType];
    if (configDefault && currentOptions?.some(opt => opt.value === configDefault)) {
      return configDefault;
    }
    // 优先级 4: 第一个选项
    return currentOptions?.[0]?.value || '';
  };

  const [value, setValue] = useState<string>(getDefaultValueByTimeType());
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!timeTypeOptionsMap && !showOnTimeType) return;
    const nextValue = getDefaultValueByTimeType();
    setValue(nextValue);
    if (dataKey && changeData) {
      changeData(dataKey, nextValue);
    }
  }, [timeType, timeTypeOptionsMap, showOnTimeType, currentOptions, dataKey, changeData, defaultValueMap]);

  // 同步外部 data[dataKey]，避免切 net/gross 等导致子树 remount 后
  // Segment UI 与共享状态脱节（再点同一选项时 by_type 不变、Service 不刷新）
  useEffect(() => {
    if (!dataKey) return;
    const savedVal = data?.[dataKey];
    if (
      savedVal &&
      savedVal !== value &&
      currentOptions?.some(opt => opt.value === savedVal)
    ) {
      setValue(savedVal);
    }
  }, [dataKey, data?.[dataKey], currentOptions, value]);

  const isSegmentVisible = useMemo(() => {
    if (!showOnTimeType) return true;
    return showOnTimeType.includes(timeType);
  }, [showOnTimeType, timeType]);

  const bubbleText = (explain: string, text: string) => (
    <CommonBubble
      content={
        explain ? (
          <p
            className="db-SwitchChart__titleBubble-infoTxt"
            // 后端返回换行符需要转成 <br/>
            dangerouslySetInnerHTML={{ __html: explain?.replaceAll('\\n', '<br/>') }}
          />
        ) : (
          ''
        )
      }
      dark
      className="metrics-segment__item-bubble"
      placement="top-start"
    >
      {text}
    </CommonBubble>
  );

  const options = useMemo(
    () =>
      currentOptions?.map((val: ISegmnetOption) => {
        const explain = isEN ? val.bubble : val.bubbleCn;
        const text = isEN ? val.name : val.nameCn;
        const baseNode = bubbleText(explain || '', text);

        // 参照 DbbSegment 增加 info 气泡图标
        let infoText = null;
        if (val.info) {
          infoText = isEN ? val.info.textEn : val.info.textCn;
        }

        // showIcon 默认为 true，保持向后兼容
        const showIcon = val.info?.showIcon !== false;

        let optionText;
        if (infoText && showIcon) {
          // 有 info 且需要 icon：文字 + icon 气泡
          optionText = (
            <div className="dbd-segment-info">
              {baseNode}
              <CommonBubble
                trigger="hover"
                arrowPointAtCenter
                placement="top-start"
                content={infoText}
                dark
              >
                <i> <SvgIcon
                  className="dbd-segment-info-icon"
                  type={value === val.value ? 'tab-tip-icon' : 'alert-circle-m'}
                /></i>
              </CommonBubble>
            </div>
          );
        } else if (infoText && !showIcon) {
          // 有 info 但不需要 icon：气泡直接包裹在 segment 文字上，不再嵌套 bubbleText 的 Bubble
          optionText = (
            <CommonBubble
              trigger="hover"
              arrowPointAtCenter
              placement="top-start"
              content={infoText}
              dark
            >
              <span>{text}</span>
            </CommonBubble>
          );
        } else {
          optionText = baseNode;
        }

        return {
          text: optionText,
          value: val.value,
        };
      }) || [],
    [currentOptions, isEN, value],
  );

  const filteredChildren = useMemo(() => {
    if (childrenRenderMode !== 'byDataKey') {
      return React.Children.toArray(children);
    }
    const matchField = childrenMatchField || 'id';
    return React.Children.toArray(children).filter((child: any) => {
      const childId = child?.props?.config?.[matchField];
      return !childId || childId === value;
    });
  }, [children, childrenRenderMode, childrenMatchField, value]);

  if (!isSegmentVisible) {
    return (
      <>
        {filteredChildren.map((child: any) =>
          React.cloneElement(child, {
            ...(child?.childProps || {}),
          }),
        )}
      </>
    );
  }

  return (
    <>
      <Segment
        className={className}
        value={value}
        style={style}
        onChange={(val: string) => {
          setValue(val);
          if (dataKey && changeData) {
            changeData(dataKey, val);
          }
        }}
        options={options}
      />
      {filteredChildren.map((child: any) =>
        React.cloneElement(child, {
          ...(child?.childProps || {}),
          segmentVal: value,
        }),
      )}
    </>
  );
};

export default SegmentCo;